import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, BookOpen } from "lucide-react";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import { getGuideList, type GuideListItem } from "./guidesData";

/** SEO impératif (form-polls n'a pas de HelmetProvider) — title + meta description. */
function usePageSeo(title: string, description: string) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content") ?? null;
    if (meta) meta.setAttribute("content", description);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, [title, description]);
}

export default function GuidesIndex() {
  const { t, i18n } = useTranslation("forms");
  const list = getGuideList(i18n.language);

  usePageSeo(t("guides.seoTitle"), t("guides.seoDescription"));

  // Catégories dérivées de la liste (préserve l'ordre, libellé déjà localisé).
  const categories: { label: string; guides: GuideListItem[] }[] = [];
  for (const g of list) {
    let cat = categories.find((c) => c.label === g.category);
    if (!cat) {
      cat = { label: g.category, guides: [] };
      categories.push(cat);
    }
    cat.guides.push(g);
  }

  return (
    <div className="flex min-h-screen-safe bg-[#030712] text-white">
      <ProductSidebar productType="form" />
      <div className="flex-1 min-w-0 flex flex-col">
        <ProductHeader productType="form" showAuth={true} />

        <main className="flex-1 max-w-4xl mx-auto w-full px-6 py-16">
          <div className="mb-12">
            <div className="flex items-center gap-2 text-violet-400 text-sm mb-4">
              <BookOpen className="w-4 h-4" />
              <span>{t("guides.badge")}</span>
            </div>
            <h1 className="text-4xl font-bold mb-4">{t("guides.title")}</h1>
            <p className="text-gray-400 text-lg max-w-2xl">
              {t("guides.subtitle")}
            </p>
          </div>

          <div className="space-y-12">
            {categories.map((cat) => (
              <section key={cat.label}>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                  {t(`guides.category.${cat.label}`, cat.label)}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {cat.guides.map((g) => (
                    <Link
                      key={g.slug}
                      to={`/guides/${g.slug}`}
                      className="group flex items-center justify-between p-5 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/10 transition-all"
                    >
                      <span className="font-medium text-white group-hover:text-violet-300 transition-colors">
                        {g.title}
                      </span>
                      <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-violet-400 transition-colors shrink-0 ml-4" />
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div className="mt-16 p-8 rounded-2xl bg-gradient-to-br from-violet-500/10 to-purple-500/10 border border-violet-500/20 text-center">
            <p className="text-lg font-semibold mb-2">{t("guides.ctaTitle")}</p>
            <p className="text-sm text-gray-400 mb-6">
              {t("guides.ctaSubtitle")}
            </p>
            <Link
              to="/create"
              className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-400 hover:to-purple-400 text-white font-medium rounded-xl transition-all shadow-lg shadow-violet-500/20"
            >
              {t("guides.ctaButton")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </main>
      </div>
    </div>
  );
}
